// scopeWords — the words of a scope DRAFT (the editor's footer, the wizard's
// review): what the person will see — "Whole project" when every present section
// is All; else the "All …" words and the counts of the picks (the draft holds ids,
// the names are the pickers'; the saved scope reads through `ScopeSummary`, by
// name), then the grants outside the viewer's reach the save keeps, as a count;
// nothing at all reads "No scope yet". And the words of a chain's place for the
// Access card's Responsibilities line: named when the viewer may see it, by its
// kind alone when not (ADR-066 §9).

import type { PersonChain } from '@ems-app/schemas/users/responses';
import type { Tr } from './access.js';
import { NO_OUTSIDE, type ScopeFormValues, type ScopeOutside } from './scope.js';

export function draftScopeWords(t: Tr, v: ScopeFormValues, opts: { vehicles: boolean; teams?: boolean; outside?: ScopeOutside }): string {
  const outside = opts.outside ?? NO_OUTSIDE;
  const kept = outside.sites + outside.depots + outside.vehicle_pools + outside.teams;
  if (v.sites.all && v.depots.all && (!opts.vehicles || v.vehicle_pools.all) && (!opts.teams || v.teams.all)) return t('scope.kind.whole');
  const parts: string[] = [];
  if (v.sites.all) parts.push(t('scope.allSites'));
  else {
    if (v.sites.zone_ids.length > 0) parts.push(t('scope.zonesCount', { count: v.sites.zone_ids.length }));
    if (v.sites.cluster_ids.length > 0) parts.push(t('scope.clustersCount', { count: v.sites.cluster_ids.length }));
    if (v.sites.site_ids.length > 0) parts.push(t('scope.sitesCount', { count: v.sites.site_ids.length }));
  }
  if (v.depots.all) parts.push(t('scope.allDepots'));
  else if (v.depots.ids.length > 0) parts.push(t('scope.depotsCount', { count: v.depots.ids.length }));
  if (opts.vehicles) {
    if (v.vehicle_pools.all) parts.push(t('scope.allPools'));
    else if (v.vehicle_pools.ids.length > 0) parts.push(t('scope.poolsCount', { count: v.vehicle_pools.ids.length }));
  }
  if (opts.teams) {
    if (v.teams.all) parts.push(t('scope.allTeams'));
    else if (v.teams.ids.length > 0) parts.push(t('scope.teamsCount', { count: v.teams.ids.length }));
  }
  if (kept > 0) parts.push(t('scope.outsideKept', { count: kept }));
  return parts.length > 0 ? parts.join(' · ') : t('scope.kind.none');
}

/** "Zone Manager · Eastern Region" — or "Zone Manager · a zone" for a place outside the viewer's reach. */
export function chainWords(t: Tr, c: PersonChain): string {
  const place = c.level === 'project' ? t('access.outside.project') : (c.scope_label ?? t(`access.outside.${c.level}`));
  return `${c.responsibility_name} · ${place}`;
}
